import type { ISuggestion, ISuggestionData } from '../dadata-service/models';
import type {
    INamePartParams,
    INameParts,
    IState,
    TDaDataNamePart,
    TGender,
    TQc,
    TStateNamePart,
    TDaDataGender,
    TDaDataFunctions,
    IValueController,
    IStateMapper,
    TCurrentSuggestion,
} from '../models';
import { GENDER_UNKNOWN, QC_FAIL } from '..';
import {
    CONCAT_FIO_RULES,
    QC_FAILED,
    QC_OK,
    DA_DATA_LAST_NAME,
    DA_DATA_FIRST_NAME,
    DA_DATA_PATRONYMIC,
    daDataToFrontGenders,
    frontToDaDataGenders,
    namePartStateConfig,
    PART_BY_SUGGESTION,
} from '../config';
import type { DaDataService } from '../dadata-service';
import type { IRenderFieldOptions } from '../components/field/models';

export const defaultCallback = () => {};

export const concatFIO = (
    concatRule: Array<TStateNamePart | undefined>,
    value: string,
    state: IState
): string => {
    return concatRule.reduce((fullFio, currentNamePart, index) => {
        const namePartValue = currentNamePart ? state[currentNamePart] : value;
        /** пробел ставим, если акк не пустой и есть либо текущее слово, либо предыдущее */
        const prevPart = index > 0 && concatRule[index - 1];
        const prevPartValue = (prevPart && state[prevPart]) || prevPart === undefined;
        const divider = fullFio && (namePartValue || prevPartValue) ? ' ' : '';

        return `${fullFio}${divider}${namePartValue}`;
    }, '');
};

export const findSuggestionByValue = (
    value: string,
    suggestions?: INamePartParams[]
): INamePartParams | undefined => {
    return suggestions?.find(item => item.value === value);
};

const mapGender = (gender: TDaDataGender): TGender => {
    return daDataToFrontGenders[gender];
};

const mapToDaDataGender = (gender: TGender): TDaDataGender => {
    return frontToDaDataGenders[gender];
};

export const mapToNamePartParams = (
    suggestions: ISuggestion[],
    namePart: TDaDataNamePart
): INamePartParams[] => {
    return suggestions.map(({ data }) => ({
        value: data[namePart],
        gender: mapGender(data.gender as TDaDataGender),
        qc: data.qc as TQc,
    }));
};

export const getFioGender = (fioParams: Array<INamePartParams | undefined>): TGender => {
    const genders = fioParams.map(item => (item?.gender ? item?.gender : GENDER_UNKNOWN));

    /** части ФИО с определенным DaData полом */
    const determinedGenders = genders.filter(
        item => !!item && item !== GENDER_UNKNOWN
    ) as TGender[];
    /** проверяем, одинаковый ли пол определился */
    const isDeterminedAllSame = determinedGenders.every(gender => gender === determinedGenders[0]);
    const isDeterminedThree = determinedGenders.length === 3;

    /** части ФИО с неопределенным DaData полом */
    const unknownGenders = genders.filter(item => !!item && item === GENDER_UNKNOWN);
    /** если 2 определены, а один нет, этого достаточно для определения */
    const isDeterminedTwo = determinedGenders.length === 2 && unknownGenders.length === 1;

    const isGenderConfirmed = isDeterminedAllSame && (isDeterminedThree || isDeterminedTwo);

    return isGenderConfirmed ? determinedGenders[0] : GENDER_UNKNOWN;
};

/** определение пола при неполном ФИО */
export const getPartialFioGender = (
    fioParams: Array<INamePartParams | undefined>,
    isLastNameFinished: boolean
): TDaDataGender => {
    if (!isLastNameFinished) {
        return GENDER_UNKNOWN;
    }

    const genders = fioParams.map(item => item?.gender);
    /** части ФИО с определенным DaData полом */
    const determinedGenders = genders.filter(
        item => !!item && item !== GENDER_UNKNOWN
    ) as TGender[];
    /** проверяем, одинаковый ли пол определился */
    const [controlGender] = determinedGenders;
    const isDeterminedAllSame = determinedGenders.every(gender => gender === controlGender);

    return isDeterminedAllSame ? mapToDaDataGender(controlGender) : GENDER_UNKNOWN;
};

export const splitFIO = (value: string | undefined): INameParts => {
    const [lastName = '', firstName = '', patronymic = '', ...restWordsArr] = value
        ? value.trim().split(' ')
        : [];
    const restWords = restWordsArr.length === 0 ? undefined : restWordsArr.join(' ');

    return { lastName, firstName, patronymic, restWords };
};

const getFIO = (state: IState): string => {
    const { firstName, lastName, patronymic } = state;

    let fio = lastName;
    fio = firstName ? `${fio} ${firstName}` : fio;

    return patronymic ? `${fio} ${patronymic}` : fio;
};

export const getSelectOptions = (state: IState): IRenderFieldOptions[] => {
    const { currentSuggestions } = state;

    if (!currentSuggestions) return [];

    const options = state[currentSuggestions];
    if (!options) return [];

    const concatRule = CONCAT_FIO_RULES[currentSuggestions] as Array<TStateNamePart | undefined>;

    const fio = getFIO(state);
    const firstOption = concatFIO(concatRule, options[0]?.value, state);
    const isOnlyOptionLessThenValue = firstOption.length <= fio.length;
    if (options.length === 1 && isOnlyOptionLessThenValue) return [];

    return options.map(({ value: optionValue }, id) => ({
        id,
        label: concatFIO(concatRule, optionValue, state),
    }));
};

export const getFioParams = (state: IState): Array<INamePartParams | undefined> => {
    const {
        firstName,
        firstNameSuggestions,
        lastName,
        lastNameSuggestions,
        patronymic,
        patronymicSuggestions,
    } = state;

    const lastNameSuggestion = findSuggestionByValue(lastName, lastNameSuggestions);
    const firstNameSuggestion = findSuggestionByValue(firstName, firstNameSuggestions);
    const patronymicSuggestion = findSuggestionByValue(patronymic, patronymicSuggestions);

    return [lastNameSuggestion, firstNameSuggestion, patronymicSuggestion];
};

export const getFioQc = (
    fioParams: Array<INamePartParams | undefined>,
    restWords?: string
): TQc => {
    /** если хоть один элемент не достоверен, то ФИО целиком недостоверно */
    const isEveryPartOk = fioParams.every(item => item && item.qc === QC_OK);
    /** для подтверждения достоверности должно быть три слова в ФИО */
    const hasThreeWords = fioParams.length === 3 && !restWords;
    const isQcOk = isEveryPartOk && hasThreeWords;

    return isQcOk ? QC_OK : QC_FAILED;
};

/** в зависимости от части имени выбирается соответствующий запрос в ДаДату */
export const daDataFunctions: TDaDataFunctions = (daDataService: DaDataService) => ({
    [DA_DATA_LAST_NAME]: daDataService.fetchLastName,
    [DA_DATA_FIRST_NAME]: daDataService.fetchFirstName,
    [DA_DATA_PATRONYMIC]: daDataService.fetchPatronymic,
});

export const updateAbortControllers = (controllers: IValueController[], value: string) => {
    /** убиваем запросы, которые ушли раньше, но еще не пришли */
    if (controllers[0].value !== value) {
        const valueIndex = controllers.findIndex(item => item.value === value);

        for (let i = 0; i < valueIndex; i++) {
            controllers[i].controller.abort();
        }
        // TODO сделать то, что хотели, только правильно
        /** убираем из массива контроллеры убитых запросов */
        // controllers.slice(valueIndex);
    }

    if (controllers[0].value === value) {
        /** убираем из массива контроллеры текущего запроса */
        controllers.shift();
    }
};

export const mapResultToState = ({ namePart, result, value }: IStateMapper): Partial<IState> => {
    const suggestions = mapToNamePartParams(result, namePart);

    return namePartStateConfig[namePart]({
        value,
        suggestions,
    });
};

const mockedRequestData = (namePart: TDaDataNamePart, value: string): ISuggestionData => {
    return {
        gender: GENDER_UNKNOWN,
        name: '',
        patronymic: '',
        qc: QC_FAIL,
        source: '',
        surname: '',
        [namePart]: value,
    };
};

export const getDefaultSuggestion = (
    namePart: TDaDataNamePart,
    value: string
): Promise<ISuggestion[]> => {
    return Promise.resolve([
        {
            // eslint-disable-next-line camelcase
            unrestricted_value: value,
            value,
            data: mockedRequestData(namePart, value),
        },
    ]);
};

export const filterByValue = (
    suggests: INamePartParams[],
    targetValue: string
): INamePartParams[] => suggests.filter(({ value }) => value === targetValue);

export const getCurrentSuggestionValue = (
    currentSuggestion: TCurrentSuggestion,
    nameParts: INameParts
): string => {
    const currentPart = PART_BY_SUGGESTION[currentSuggestion];

    return nameParts[currentPart];
};
